import { LegendItem } from 'chart.js';

import { FormulaType } from '../constants/formulaType';

import { ApprovedFormulaState, FormulaStatus } from './formula.model';
import { ViewByName } from './live-messages.model';
import {
    METRIC_NAMES,
    PerformanceMetricsModel,
    SUCCESS_METRICS,
    topPerformingMetricsCount,
    topPerformingMetricsPercent,
    PerformanceMetricsRawModel,
} from './metrics.model';
import { BasePaginatedResponse } from './pagination';
import { AllVariantTypes } from './variant.model';

export interface LabelModel {
    id: number;
    label_name: string;
    label_description?: string;
    label_example?: string;
    variant_type?: string;
    created_at?: Date;
    content_count?: number;
    alternates_count?: number;
    formulas_count?: number;
    archived_at?: string | null;
}

export interface LabelOption {
    id: number | null;
    label_name: string;
}

export interface LabelBaseModel {
    label_name: string;
    label_description?: string;
    label_example?: string;
    variant_type?: string;
}

export interface VarintLabel {
    id: number;
    label_name: string;
    label_description?: string;
    label_example?: string;
    weight?: number;
}
export enum VariantTypeCategory {
    Greeting = 'Greeting',
    Offering = 'Offering',
    ValueProposition = 'ValueProposition',
    CallToAction = 'CallToAction',
    Incentive = 'Incentive',
    Other = 'Other',
    Tone = 'Tone',
    Image = 'Image',
    All = 'All',
}
export type LabelChannelCountModel = { [key in AllVariantTypes]: number };

export interface GamificationItemModel extends LabelModel {
    count: number;
}

export interface LabelVariantTypeModel {
    id: number;
    label_name: string;
    variant_type: string;
    alternates_count?: number;
    formulas_count?: number;
}

export interface LabelWithVariantsModel {
    id: number;
    label_name: string;
    alternates: string[];
}
export interface LabelAlternateModel {
    text: string;
    label_name: string;
    index: number;
    label_id: number;
}
export interface LabelAlternateListModel {
    [label_name: string]: LabelAlternateModel[];
}

export const UnlabelledLabel: LabelModel = {
    id: -1,
    label_name: 'Unlabelled',
    label_description:
        'Not ready to personalize yet? Add unlabeled content here.',
};

export interface LabelAlternateFormulaDetailModel {
    id: number;
    name: string;
    status: FormulaStatus;
    active_state: FormulaStatus | ApprovedFormulaState;
    surface_id?: string;
}

export interface LabelFormulaDetailModel
    extends LabelAlternateFormulaDetailModel {
    message_count: number;
}

export interface LabelAlternateDetailModel {
    id: number;
    content: string;
    formula: LabelAlternateFormulaDetailModel;
    alternate_variant_id?: string;
}

export interface LabelDetailsModel extends LabelModel {
    content_count: number;
    variant_type?: string;
    component_type_id?: string;
    weight?: number;
}
export interface LabelDetailsVariantModel extends LabelModel {
    variant_type: string;
    content_count: number;
}

export interface LabelMetricsMap {
    [key: number]: PerformanceMetricsModel;
}

export interface LabelRawMetricsMap {
    [key: number]: PerformanceMetricsRawModel;
}

export interface LabelMetricsModel {
    metrics: LabelMetricsMap;
    raw_metrics: LabelRawMetricsMap;
    latest_date?: string;
}

export interface LabelDetailMetricsCombinedModel
    extends PerformanceMetricsModel,
        LabelDetailsModel {}

export const numberOfAlternatesPerLabel = 5;
export const numberOfStarsPerLabel = 5;
export const numberOfBarsPerLabel = 3;

export interface LabelUpdateModel {
    id: number;
    data: {
        label_name?: string;
        label_description?: string;
        label_example?: string;
        weight?: number;
    };
}

export interface LabelCreateModel {
    label_name: string;
    variant_type: string;
    label_description?: string;
    label_example?: string;
}

export type LabelVariantMetrics = {
    [key in SUCCESS_METRICS]?: number;
};

export type LabelVariantMetricsDataModel = LabelAlternateDetailModel &
    LabelVariantMetrics;

export const LABEL_METRICS_MENU_POPOVER_ID = 'label-metrics-popover';

export type LabelPerformanceMetricRawModel = {
    [metricKey: string]: {
        [date: string]: number;
    };
};

export type LabelPerformanceMetricsAggregatedModel = {
    metrics: {
        [labelId: string]: Record<METRIC_NAMES, number>;
    };
    latest_date?: string;
};

export const LABEL_PERFORMANCE_METRICS = {
    [ViewByName.COUNT]: [
        ...topPerformingMetricsCount,
        METRIC_NAMES.NUM_MESSAGES_SENT_COUNT,
    ],
    [ViewByName.RATE]: [
        ...topPerformingMetricsPercent,
        METRIC_NAMES.NUM_MESSAGES_SENT_PERCENTAGE,
    ],
};

export const LABEL_PERFORMANCE_RAW_METRICS = {
    [ViewByName.COUNT]: [
        METRIC_NAMES.NOTIFICATION_OPENED_SUCCESSFUL_MESSAGES_SENT_COUNT,
    ],
    [ViewByName.RATE]: [
        METRIC_NAMES.NOTIFICATION_OPENED_SUCCESSFUL_MESSAGES_SENT_PERCENTAGE,
    ],
};

export const LABEL_PERFORMANCE_CHART_METRICS = {
    [ViewByName.COUNT]: [
        METRIC_NAMES.ADDED_TO_CART_SUCCESSFUL_MESSAGES_SENT_COUNT,
        METRIC_NAMES.NUM_MESSAGES_SENT_COUNT,
    ],
    [ViewByName.RATE]: [
        METRIC_NAMES.ADDED_TO_CART_SUCCESSFUL_MESSAGES_SENT_PERCENTAGE,
        METRIC_NAMES.NUM_MESSAGES_SENT_PERCENTAGE,
    ],
};

interface DataPointModel {
    x: string | number | undefined;
    y: string | number | undefined;
    r: string | number | undefined;
    name?: string;
    labelId?: number;
    content_count?: number;
}

export interface LabelDetailsChartDatasetModel {
    label: string;
    backgroundColor: string[];
    borderColor: string[];
    variant_type: string;
    data: DataPointModel[];
    datasetIndex: number;
}

export interface LabelDetailChartCustomLegendItem extends LegendItem {
    variant_type?: string;
}

export enum LabelDetailsChartView {
    GRAPH_TABLE = 'graph_table',
    ONLY_TABLE = 'only_table',
    ONLY_GRAPH = 'only_graph',
}

export enum LabelUsageInContent {
    All = 'All',
    MostUsed = 'MostUsed',
    FrequentlyUsed = 'FrequentlyUsed',
    ModeratelyUsed = 'ModeratelyUsed',
    OccasionallyUsed = 'OccasionallyUsed',
    RarelyUsed = 'RarelyUsed',
    NotUsed = 'NotUsed',
}

export enum DefaultOptionForFilter {
    All = 'All',
}
export enum LabelUsageInContentLabel {
    All = 'All',
    MostUsed = 'Most used',
    FrequentlyUsed = 'Frequently used',
    ModeratelyUsed = 'Moderately used',
    OccasionallyUsed = 'Occasionally used',
    RarelyUsed = 'Rarely used',
    NotUsed = 'Not used',
}

type UsageInContentValueLabelMapping = {
    [key in LabelUsageInContent]: LabelUsageInContentLabel;
};

export const USAGE_IN_CONTENT_VALUE_LABEL_MAPPING: UsageInContentValueLabelMapping =
    {
        [LabelUsageInContent.All]: LabelUsageInContentLabel.All,
        [LabelUsageInContent.MostUsed]: LabelUsageInContentLabel.MostUsed,
        [LabelUsageInContent.FrequentlyUsed]:
            LabelUsageInContentLabel.FrequentlyUsed,
        [LabelUsageInContent.ModeratelyUsed]:
            LabelUsageInContentLabel.ModeratelyUsed,
        [LabelUsageInContent.OccasionallyUsed]:
            LabelUsageInContentLabel.OccasionallyUsed,
        [LabelUsageInContent.RarelyUsed]: LabelUsageInContentLabel.RarelyUsed,
        [LabelUsageInContent.NotUsed]: LabelUsageInContentLabel.NotUsed,
    };

export enum LabelMessageStatus {
    All = 'All',
    Live = 'Live',
    Expired = 'Expired',
    Scheduled = 'Scheduled',
    Archived = 'Archived',
    Review = 'Review',
    Draft = 'Draft',
}

export const LabelMessageStatusOptions = [
    { value: LabelMessageStatus.All, label: LabelMessageStatus.All },
    { value: LabelMessageStatus.Live, label: LabelMessageStatus.Live },
    { value: LabelMessageStatus.Expired, label: LabelMessageStatus.Expired },
    {
        value: LabelMessageStatus.Scheduled,
        label: LabelMessageStatus.Scheduled,
    },
    { value: LabelMessageStatus.Archived, label: LabelMessageStatus.Archived },
    { value: LabelMessageStatus.Draft, label: LabelMessageStatus.Draft },
    { value: LabelMessageStatus.Review, label: LabelMessageStatus.Review },
];

export enum LabelSendingCriteria {
    All = 'All',
    Scheduled = 'Scheduled',
    Triggered = 'Triggered',
}

export const LabelSendingCriteriaOptions = [
    { value: LabelSendingCriteria.All, label: LabelSendingCriteria.All },
    {
        value: LabelSendingCriteria.Scheduled,
        label: LabelSendingCriteria.Scheduled,
    },
    {
        value: LabelSendingCriteria.Triggered,
        label: LabelSendingCriteria.Triggered,
    },
];

export const FormulaTypeOptions = [
    { value: FormulaType.contentCards, label: 'Content card' },
    { value: FormulaType.email, label: 'Email' },
    { value: FormulaType.mobilePush, label: 'Push' },
    { value: FormulaType.sms, label: 'SMS' },
    { value: FormulaType.surface, label: 'Surface' },
    { value: FormulaType.webPush, label: 'Web push' },
    { value: FormulaType.whatsapp, label: 'Whatsapp' },
];

export const DEFAULT_USAGE_IN_CONTENT_FOR_QUERY: LabelUsageInContent[] = [
    LabelUsageInContent.All,
];

export const DEFAULT_MESSAGE_STATUS_FOR_QUERY: LabelMessageStatus[] = [
    LabelMessageStatus.Live,
];

export const DEFAULT_SENDING_CRITERIA_FOR_QUERY: LabelSendingCriteria[] = [
    LabelSendingCriteria.All,
];
export interface LabelFilterFormikValues {
    usage_in_content: LabelUsageInContent[];
    variant_types: AllVariantTypes[];
    message_status: LabelMessageStatus[];
    sending_criteria: LabelSendingCriteria[];
}

export type LabelPerformanceFiltersValues =
    | string
    | LabelUsageInContent
    | LabelMessageStatus
    | LabelSendingCriteria;

export enum LabelPerformanceTabOptions {
    RelatedAlternatePerformance = 'related_alternate_performance',
    RelatedMessagePerformance = 'related_message_performance',
    InActiveAlternates = 'inactive_alternates',
}

export interface RelatedPerformanceBaseModal {
    id: number;
    name: string;
    related_alternates: number;
    message_type: string;
    message_count: number;
}

export interface RelatedMessagePerformanceModel
    extends PerformanceMetricsModel {
    formula: RelatedPerformanceBaseModal;
}

export interface RelatedMessagesPerformanceFilterParams {
    variant_type: string;
    nDays: number;
}

export interface PaginatedRelatedMessagesPerformanceModel
    extends BasePaginatedResponse<RelatedMessagePerformanceModel> {}

export enum EmailSpecificOptions {
    Active = 'active',
    InActive = 'inActive',
}

export interface RelatedAlternateBaseModal {
    id: number;
    alternate_text: string;
    name: string;
    message_type: string;
}

export interface RelatedAlternatePerformanceModel
    extends PerformanceMetricsModel {
    alternate: RelatedAlternateBaseModal;
}

export interface PaginatedRelatedAlternatePerformanceModel
    extends BasePaginatedResponse<RelatedAlternatePerformanceModel> {}

export type StatColumnsModal =
    | RelatedMessagePerformanceModel
    | RelatedAlternatePerformanceModel;

export enum RelatedMessagePerformanceTab {
    LabelAssociatedPerformance = 'label_associated_performance',
    TotalMessagePerformance = 'total_message_performance',
}

export interface InactiveLabeledAlternateModal {
    id: number;
    content: string;
    name: string;
    message_type: string;
    active_state: string;
    updated_at: string;
}

export interface PieChartModal {
    value: number;
    name: string;
    sliceColor?: string;
    dotColor?: string;
}

export const PieChartColorOpacity = {
    Triggered: '80',
    General: '60',
};
export interface FormulaTriggerCountResponseModal {
    formulas_with_trigger: number;
    formulas_without_trigger: number;
}

export enum FormulaTriggerType {
    Triggered = 'Triggered',
    General = 'General',
}

export interface LabelFormValues {
    variantType: string;
    labelName: string;
    labelDescription?: string;
    labelExample?: string;
}

export interface ParsedLabel {
    type: string;
    ids: string[];
}

export type ParsedLabelsType = [] | [ParsedLabel, ParsedLabel];

export enum DatasetLabelColumnOptions {
    RetainLabelColumn = 'retain_label_column',
    DeleteLabelColumn = 'delete_label_column',
}
